import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Snackbar,
  Alert,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  FilterList as FilterIcon,
  VpnKey as KeyIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Assessment as ReportsIcon,
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { userApi, adminToolsApi, User, UserFilters } from '../services/api';
import { authService } from '../services/auth';
import { UserDialog } from './UserDialog';
import { UserHistoryDialog } from './UserHistoryDialog';
import UserTasksDialog from './UserTasksDialog';
import { config } from '../config';

interface UserListProps {
  onLogout: () => void;
  onShowReports: () => void;
}

export const UserList: React.FC<UserListProps> = ({ onLogout, onShowReports }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    organization: '',
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [orderBy, setOrderBy] = useState<string>('last_name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<User | null>(null);
  const [passwordResetForm, setPasswordResetForm] = useState({
    customPassword: '',
    alternateEmail: '',
    bccEmail: '',
    appUrl: 'lms_dev',
    useCustomPassword: false,
    permanentPassword: true
  });
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'inspector',
    region: '',
    zipCode: '',
    organizationName: '',
    city: '',
    state: '',
    lmsEnabled: true,
    rmsEnabled: false,
    rmsUnlocked: false,
    sendWelcomeEmail: true,
    alternateEmail: '',
    bccEmail: '',
    appUrl: 'lms_dev',
    customPassword: '',
  });
  const [useCustomPasswordOnboarding, setUseCustomPasswordOnboarding] = useState(false);
  const [permanentPasswordOnboarding, setPermanentPasswordOnboarding] = useState(true);
  const [onboardingResult, setOnboardingResult] = useState<any>(null);
  const [showOnboardingPassword, setShowOnboardingPassword] = useState(false);
  const [statistics, setStatistics] = useState<{
    byOrganization: { [key: string]: number };
    byRole: { [key: string]: number };
    byLocation: { [key: string]: number };
  }>({
    byOrganization: {},
    byRole: {},
    byLocation: {}
  });
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [userToPause, setUserToPause] = useState<User | null>(null);
  const [pauseAction, setPauseAction] = useState<'pause' | 'unpause'>('pause');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [userForHistory, setUserForHistory] = useState<User | null>(null);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);
  const [userForTasks, setUserForTasks] = useState<User | null>(null);

  // Available filter options
  const organizations = [
    'ARCH Systems', 'CGS', 'CRI', 'FCSO', 'IDS', 'NGS',
    'Novitas Solutions', 'PALMETTO GBA', 'WPSIC'
  ];
  const roles = ['inspector', 'mac_npe_reviewers', 'admin', 'program_admin'];

  const fetchStatistics = async () => {
    try {
      // Fetch all users for statistics (without pagination)
      const response = await userApi.getUsers(1, 10000, {});
      const allUsers = response.users;

      // Calculate statistics
      const byOrganization: { [key: string]: number } = {};
      const byRole: { [key: string]: number } = {};
      const byLocation: { [key: string]: number } = {};

      allUsers.forEach(user => {
        // Organization stats
        const org = user.organization_name || 'No Organization';
        byOrganization[org] = (byOrganization[org] || 0) + 1;

        // Role stats
        const role = user.role || 'No Role';
        byRole[role] = (byRole[role] || 0) + 1;

        // Location stats (by state)
        const location = user.state || 'No Location';
        byLocation[location] = (byLocation[location] || 0) + 1;
      });

      setStatistics({ byOrganization, byRole, byLocation });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      console.log('🔍 FETCHING USERS:', {
        page: page + 1,
        rowsPerPage,
        filters,
        sortBy: orderBy,
        sortOrder: order,
        url: `${config.api.baseUrl}/users?page=${page + 1}&limit=${rowsPerPage}`
      });

      const response = await userApi.getUsers(page + 1, rowsPerPage, {
        ...filters,
        sortBy: orderBy,
        sortOrder: order
      });

      console.log('✅ API RESPONSE:', {
        usersCount: response.users.length,
        total: response.pagination.total,
        pagination: response.pagination,
        firstUser: response.users[0],
        allUsers: response.users
      });

      setUsers(response.users);
      setTotal(response.pagination.total);
    } catch (error) {
      console.error('❌ ERROR FETCHING USERS:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStatistics();
  }, [page, rowsPerPage, filters, orderBy, order]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof UserFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({ search: '', organization: '', role: '' });
    setPage(0);
  };

  const handleToggleField = async (userId: string, field: 'lms_enabled' | 'rms_enabled' | 'rms_unlocked' | 'is_active', currentValue: boolean) => {
    try {
      await userApi.updateUser(userId, { [field]: !currentValue });
      fetchUsers();
      fetchStatistics();
      setToast({
        open: true,
        message: `User ${field.replace('_', ' ')} updated successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setToast({
        open: true,
        message: `Failed to update ${field.replace('_', ' ')}`,
        severity: 'error'
      });
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await userApi.deleteUser(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
      fetchStatistics();
      setToast({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      setToast({
        open: true,
        message: 'Failed to delete user',
        severity: 'error'
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handlePasswordResetClick = (user: User) => {
    setUserToReset(user);
    setPasswordResetForm({
      customPassword: '',
      alternateEmail: '',
      bccEmail: '',
      appUrl: 'lms_dev',
      useCustomPassword: false,
      permanentPassword: true
    });
    setPasswordResetDialogOpen(true);
  };

  const handlePasswordResetConfirm = async () => {
    if (!userToReset) return;

    try {
      const requestBody: any = {
        email: userToReset.email,
        send_email: true,
        app_url: passwordResetForm.appUrl,
        permanent_password: passwordResetForm.permanentPassword
      };

      // Add custom password if provided
      if (passwordResetForm.useCustomPassword && passwordResetForm.customPassword.trim()) {
        requestBody.custom_password = passwordResetForm.customPassword.trim();
      }

      // Add alternate email if provided
      if (passwordResetForm.alternateEmail.trim()) {
        requestBody.alternate_email = passwordResetForm.alternateEmail.toLowerCase().trim();
      }

      // Add BCC email if provided
      if (passwordResetForm.bccEmail.trim()) {
        requestBody.bcc_email = passwordResetForm.bccEmail.toLowerCase().trim();
      }

      const response = await fetch(`${config.api.baseUrl}/admin-tools/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordResetDialogOpen(false);
        setToast({
          open: true,
          message: `Password reset successfully. New password: ${data.new_password}`,
          severity: 'success'
        });
        setUserToReset(null);
        setPasswordResetForm({
          customPassword: '',
          alternateEmail: '',
          bccEmail: '',
          appUrl: 'lms_dev',
          useCustomPassword: false,
          permanentPassword: true
        });
      } else {
        setToast({
          open: true,
          message: data.message || 'Failed to reset password',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setToast({
        open: true,
        message: 'Failed to reset password',
        severity: 'error'
      });
    }
  };

  const handlePasswordResetCancel = () => {
    setPasswordResetDialogOpen(false);
    setUserToReset(null);
    setPasswordResetForm({
      customPassword: '',
      alternateEmail: '',
      bccEmail: '',
      appUrl: 'lms_dev',
      useCustomPassword: false,
      permanentPassword: true
    });
  };

  const handlePauseClick = (user: User) => {
    setUserToPause(user);
    setPauseAction('pause');
    setPauseDialogOpen(true);
  };

  const handleUnpauseClick = (user: User) => {
    setUserToPause(user);
    setPauseAction('unpause');
    setPauseDialogOpen(true);
  };

  const handlePauseConfirm = async () => {
    if (!userToPause) return;

    try {
      const endpoint = pauseAction === 'pause' ? 'pause-user' : 'unpause-user';
      const response = await adminToolsApi[pauseAction === 'pause' ? 'pauseUser' : 'unpauseUser'](userToPause.email);
      const data = response;

      if (data.success) {
        setPauseDialogOpen(false);
        setUserToPause(null);
        fetchUsers();
        fetchStatistics();

        // Build success message
        let successMessage = '';
        if (pauseAction === 'pause') {
          const taskCount = data.tasks_unassigned || 0;
          if (taskCount > 0) {
            successMessage = `User paused successfully - ${taskCount} task${taskCount !== 1 ? 's' : ''} unassigned and returned to ready status`;
          } else {
            successMessage = 'User paused successfully - no active tasks to unassign';
          }
        } else {
          successMessage = 'User unpaused successfully - user can sign in with existing password';
        }

        setToast({
          open: true,
          message: successMessage,
          severity: 'success'
        });
      } else {
        setToast({
          open: true,
          message: data.message || `Failed to ${pauseAction} user`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error(`Error ${pauseAction}ing user:`, error);
      setToast({
        open: true,
        message: `Failed to ${pauseAction} user`,
        severity: 'error'
      });
    }
  };

  const handlePauseCancel = () => {
    setPauseDialogOpen(false);
    setUserToPause(null);
  };

  const handleOnboardingOpen = () => {
    setOnboardingDialogOpen(true);
    setOnboardingResult(null);
    setShowOnboardingPassword(false);
  };

  const handleOnboardingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOnboardingResult(null);

    try {
      const requestBody: any = {
        email: onboardingForm.email.toLowerCase(),
        first_name: onboardingForm.firstName,
        last_name: onboardingForm.lastName,
        role: onboardingForm.role,
        region: onboardingForm.region || null,
        zip_code: onboardingForm.zipCode || null,
        organization_name: onboardingForm.organizationName || null,
        city: onboardingForm.city || null,
        state: onboardingForm.state || null,
        lms_enabled: onboardingForm.lmsEnabled,
        rms_enabled: onboardingForm.rmsEnabled,
        rms_unlocked: onboardingForm.rmsUnlocked,
        send_welcome_email: onboardingForm.sendWelcomeEmail,
        app_url: onboardingForm.appUrl,
        permanent_password: permanentPasswordOnboarding,
      };

      // Add custom password if provided
      if (useCustomPasswordOnboarding && onboardingForm.customPassword.trim()) {
        requestBody.custom_password = onboardingForm.customPassword.trim();
      }

      // Add alternate email if provided
      if (onboardingForm.alternateEmail.trim()) {
        requestBody.alternate_email = onboardingForm.alternateEmail.toLowerCase().trim();
      }

      // Add BCC email if provided
      if (onboardingForm.bccEmail.trim()) {
        requestBody.bcc_email = onboardingForm.bccEmail.toLowerCase().trim();
      }

      const response = await fetch(`${config.api.baseUrl}/admin-tools/onboard-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOnboardingResult(data);
        setToast({
          open: true,
          message: 'User onboarded successfully!',
          severity: 'success'
        });
        // Reset form
        setOnboardingForm({
          email: '',
          firstName: '',
          lastName: '',
          role: 'inspector',
          region: '',
          zipCode: '',
          organizationName: '',
          city: '',
          state: '',
          lmsEnabled: true,
          rmsEnabled: false,
          rmsUnlocked: false,
          sendWelcomeEmail: true,
          alternateEmail: '',
          bccEmail: '',
          appUrl: 'lms_dev',
          customPassword: '',
        });
        setUseCustomPasswordOnboarding(false);
        setPermanentPasswordOnboarding(true);
        fetchUsers(); // Refresh the user list
      } else {
        setToast({
          open: true,
          message: data.message || 'Failed to onboard user',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error onboarding user:', error);
      setToast({
        open: true,
        message: 'Failed to onboard user',
        severity: 'error'
      });
    }
  };

  const handleOnboardingCancel = () => {
    setOnboardingDialogOpen(false);
    setOnboardingResult(null);
    setOnboardingForm({
      email: '',
      firstName: '',
      lastName: '',
      role: 'inspector',
      region: '',
      zipCode: '',
      organizationName: '',
      city: '',
      state: '',
      lmsEnabled: true,
      rmsEnabled: false,
      rmsUnlocked: false,
      sendWelcomeEmail: true,
      alternateEmail: '',
      bccEmail: '',
      appUrl: 'lms_dev',
      customPassword: '',
    });
    setUseCustomPasswordOnboarding(false);
    setPermanentPasswordOnboarding(true);
    setShowOnboardingPassword(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setToast({
      open: true,
      message: 'Copied to clipboard!',
      severity: 'info'
    });
  };

  const handleDialogClose = (refresh: boolean) => {
    setDialogOpen(false);
    setSelectedUser(null);
    if (refresh) {
      fetchUsers();
      fetchStatistics();
      setToast({
        open: true,
        message: selectedUser ? 'User updated successfully' : 'User created successfully',
        severity: 'success'
      });
    }
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const prepareChartData = (data: { [key: string]: number }, limit: number = 10) => {
    const sorted = Object.entries(data)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit);

    return sorted.map(([name, value]) => ({ name, value }));
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#C0C0C0', '#8DD1E1'];

  const handleLogout = () => {
    authService.logout();
    onLogout();
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            CMS Roster Management
          </Typography>
          <Button
            color="inherit"
            startIcon={<ReportsIcon />}
            onClick={onShowReports}
            sx={{ mr: 2 }}
          >
            Reports
          </Button>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {localStorage.getItem('authUser')}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Users ({total})</Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<PersonAddIcon />}
            onClick={handleOnboardingOpen}
          >
            Onboard New User
          </Button>
        </Box>

        {/* Statistics Charts */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom align="center">
                  Users by Organization
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={prepareChartData(statistics.byOrganization, 10)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {prepareChartData(statistics.byOrganization, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom align="center">
                  Users by Role
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={prepareChartData(statistics.byRole, 10)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {prepareChartData(statistics.byRole, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom align="center">
                  Users by Location (Top 10)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={prepareChartData(statistics.byLocation, 10)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {prepareChartData(statistics.byLocation, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ ml: 'auto' }}
            >
              Clear All
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Email, name, or organization"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={filters.organization}
                  label="Organization"
                  onChange={(e) => handleFilterChange('organization', e.target.value)}
                >
                  <MenuItem value="">All Organizations</MenuItem>
                  {organizations.map(org => (
                    <MenuItem key={org} value={org}>{org}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="inspector">Inspector</MenuItem>
                  <MenuItem value="mac_npe_reviewers">MAC NPE Reviewers</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="program_admin">Program Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>Personal Email</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'last_name'}
                    direction={orderBy === 'last_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('last_name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'organization_name'}
                    direction={orderBy === 'organization_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('organization_name')}
                  >
                    Organization
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'role'}
                    direction={orderBy === 'role' ? order : 'asc'}
                    onClick={() => handleRequestSort('role')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'state'}
                    direction={orderBy === 'state' ? order : 'asc'}
                    onClick={() => handleRequestSort('state')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">
                  <Tooltip title="Learning Management System Access">
                    <span>LMS</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Records Management System Access">
                    <span>RMS</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="RMS Unlocked for Editing">
                    <span>RMS Unlock</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.personal_email || '-'}</TableCell>
                  <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                  <TableCell>{user.organization_name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role || '-'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {user.city && user.state ? `${user.city}, ${user.state}` : user.zip_code || '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={user.is_active}
                      onChange={() => handleToggleField(user.id, 'is_active', user.is_active)}
                      color="success"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={user.lms_enabled}
                      onChange={() => handleToggleField(user.id, 'lms_enabled', user.lms_enabled)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={user.rms_enabled}
                      onChange={() => handleToggleField(user.id, 'rms_enabled', user.rms_enabled)}
                      color="secondary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={user.rms_unlocked}
                      onChange={() => handleToggleField(user.id, 'rms_unlocked', user.rms_unlocked)}
                      color="warning"
                      size="small"
                      disabled={!user.rms_enabled}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Reset Password">
                      <IconButton onClick={() => handlePasswordResetClick(user)} size="small" color="primary">
                        <KeyIcon />
                      </IconButton>
                    </Tooltip>
                    {user.is_active ? (
                      <Tooltip title="Pause User (Disable Login)">
                        <IconButton onClick={() => handlePauseClick(user)} size="small" color="warning">
                          <PauseIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Unpause User (Enable Login)">
                        <IconButton onClick={() => handleUnpauseClick(user)} size="small" color="success">
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Change History">
                      <IconButton onClick={() => {
                        setUserForHistory(user);
                        setHistoryDialogOpen(true);
                      }} size="small" color="info">
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Tasks">
                      <IconButton onClick={() => {
                        setUserForTasks(user);
                        setTasksDialogOpen(true);
                      }} size="small" color="primary">
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit User">
                      <IconButton onClick={() => handleEditUser(user)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton onClick={() => handleDeleteClick(user)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Container>

      <UserDialog
        open={dialogOpen}
        user={selectedUser}
        onClose={handleDialogClose}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete user <strong>{userToDelete?.email}</strong>?
            <br />
            <br />
            Name: {userToDelete?.first_name} {userToDelete?.last_name}
            <br />
            Organization: {userToDelete?.organization_name || 'N/A'}
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause/Unpause User Dialog */}
      <Dialog
        open={pauseDialogOpen}
        onClose={handlePauseCancel}
        aria-labelledby="pause-dialog-title"
        aria-describedby="pause-dialog-description"
      >
        <DialogTitle id="pause-dialog-title">
          {pauseAction === 'pause' ? 'Pause User' : 'Unpause User'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="pause-dialog-description">
            {pauseAction === 'pause' ? (
              <>
                Are you sure you want to <strong>pause</strong> user <strong>{userToPause?.email}</strong>?
                <br />
                <br />
                Name: {userToPause?.first_name} {userToPause?.last_name}
                <br />
                Organization: {userToPause?.organization_name || 'N/A'}
                <br />
                <br />
                <strong>What happens when you pause a user:</strong>
                <br />
                • User will be marked as inactive in the database
                <br />
                • User will be disabled in AWS Cognito
                <br />
                • User will NOT be able to sign in to LMS or RMS
                <br />
                • All active sessions will be revoked
                <br />
                • <strong>All assigned tasks</strong> (ready, assigned, accepted, in_progress, hold) will be <strong>unassigned</strong>
                <br />
                • Tasks will be returned to "ready" status for reassignment
                <br />
                • Completed/canceled tasks will remain unchanged (historical data)
                <br />
                • All task unassignments will be logged in the audit trail
                <br />
                • User data will be preserved (not deleted)
                <br />
                <br />
                You can unpause the user later to restore access.
              </>
            ) : (
              <>
                Are you sure you want to <strong>unpause</strong> user <strong>{userToPause?.email}</strong>?
                <br />
                <br />
                Name: {userToPause?.first_name} {userToPause?.last_name}
                <br />
                Organization: {userToPause?.organization_name || 'N/A'}
                <br />
                <br />
                <strong>What happens when you unpause a user:</strong>
                <br />
                • User will be marked as active in the database
                <br />
                • User will be enabled in AWS Cognito
                <br />
                • User WILL be able to sign in to LMS and RMS
                <br />
                • User can sign in with their <strong>existing password</strong> (no password reset needed)
                <br />
                • New access tokens will be issued upon sign-in
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePauseCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handlePauseConfirm}
            color={pauseAction === 'pause' ? 'warning' : 'success'}
            variant="contained"
            autoFocus
          >
            {pauseAction === 'pause' ? 'Pause User' : 'Unpause User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog
        open={passwordResetDialogOpen}
        onClose={handlePasswordResetCancel}
        aria-labelledby="password-reset-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="password-reset-dialog-title">
          Reset Password
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Reset password for <strong>{userToReset?.email}</strong>
            <br />
            Name: {userToReset?.first_name} {userToReset?.last_name}
          </DialogContentText>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* App URL Selection */}
            <TextField
              select
              label="Application URL"
              value={passwordResetForm.appUrl}
              onChange={(e) => setPasswordResetForm({ ...passwordResetForm, appUrl: e.target.value })}
              fullWidth
              required
              SelectProps={{
                native: true,
              }}
              helperText="Select which application URL to include in the email"
            >
              <option value="lms_dev">LMS DEV (https://lms.archsystemsinc.com)</option>
              <option value="lms_prod">LMS PROD (https://lms.eastern.archsystemsinc.com)</option>
              <option value="rms_dev">RMS DEV (https://rms.archsystemsinc.com)</option>
              <option value="rms_prod">RMS PROD (https://rms.eastern.archsystemsinc.com)</option>
            </TextField>

            {/* Password Type Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={passwordResetForm.permanentPassword}
                  onChange={(e) => setPasswordResetForm({ ...passwordResetForm, permanentPassword: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {passwordResetForm.permanentPassword ? '✅ Permanent Password' : '⚠️ Temporary Password'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {passwordResetForm.permanentPassword
                      ? 'User can keep this password'
                      : 'User must change password on first login'}
                  </Typography>
                </Box>
              }
            />

            {/* Custom Password Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={passwordResetForm.useCustomPassword}
                  onChange={(e) => setPasswordResetForm({ ...passwordResetForm, useCustomPassword: e.target.checked })}
                  color="primary"
                />
              }
              label="Use Custom Password"
            />

            {/* Custom Password Field */}
            {passwordResetForm.useCustomPassword && (
              <TextField
                label="Custom Password"
                type="text"
                fullWidth
                variant="outlined"
                value={passwordResetForm.customPassword}
                onChange={(e) => setPasswordResetForm({ ...passwordResetForm, customPassword: e.target.value })}
                helperText="Enter a custom password or leave empty for auto-generated"
              />
            )}

            {/* Alternate Email */}
            <TextField
              label="Alternate Email (Optional)"
              type="email"
              fullWidth
              variant="outlined"
              value={passwordResetForm.alternateEmail}
              onChange={(e) => setPasswordResetForm({ ...passwordResetForm, alternateEmail: e.target.value })}
              helperText="📧 Send the password to this email instead of the user's login email"
              placeholder="personal.email@gmail.com"
            />

            {/* BCC Email */}
            <TextField
              label="BCC Email (Optional)"
              type="email"
              fullWidth
              variant="outlined"
              value={passwordResetForm.bccEmail}
              onChange={(e) => setPasswordResetForm({ ...passwordResetForm, bccEmail: e.target.value })}
              helperText="📋 Send a blind carbon copy to this email address"
              placeholder="admin@archsystemsinc.com"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordResetCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handlePasswordResetConfirm} color="primary" variant="contained">
            Reset Password & Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Onboarding Dialog */}
      <Dialog
        open={onboardingDialogOpen}
        onClose={handleOnboardingCancel}
        aria-labelledby="onboarding-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="onboarding-dialog-title">
          Onboard New User
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Create a new user account with all required information
          </DialogContentText>

          <Box component="form" onSubmit={handleOnboardingSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="First Name"
                    value={onboardingForm.firstName}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, firstName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Last Name"
                    value={onboardingForm.lastName}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, lastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    label="Email (Login Email)"
                    value={onboardingForm.email}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, email: e.target.value })}
                    placeholder="user@archsystemsinc.com"
                    helperText="The email address the user will use to log in"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Application URL"
                    value={onboardingForm.appUrl}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, appUrl: e.target.value })}
                    required
                    SelectProps={{
                      native: true,
                    }}
                    helperText="🔗 Select which application URL to include in the welcome email"
                  >
                    <option value="lms_dev">LMS DEV (https://lms.archsystemsinc.com)</option>
                    <option value="lms_prod">LMS PROD (https://lms.eastern.archsystemsinc.com)</option>
                    <option value="rms_dev">RMS DEV (https://rms.archsystemsinc.com)</option>
                    <option value="rms_prod">RMS PROD (https://rms.eastern.archsystemsinc.com)</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Alternate Email (Optional)"
                    value={onboardingForm.alternateEmail}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, alternateEmail: e.target.value })}
                    placeholder="personal.email@gmail.com"
                    helperText="📧 Send the welcome email to this address instead of the login email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="email"
                    label="BCC Email (Optional)"
                    value={onboardingForm.bccEmail}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, bccEmail: e.target.value })}
                    placeholder="admin@archsystemsinc.com"
                    helperText="📋 Send a blind carbon copy to this email address"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Custom Password Option */}
            <Box sx={{ border: '1px solid #90CAF9', borderRadius: 1, p: 2, bgcolor: '#E3F2FD' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomPasswordOnboarding}
                    onChange={(e) => {
                      setUseCustomPasswordOnboarding(e.target.checked);
                      if (!e.target.checked) setOnboardingForm({ ...onboardingForm, customPassword: '' });
                    }}
                    color="primary"
                  />
                }
                label="🔑 Set Custom User-Friendly Password"
              />
              {useCustomPasswordOnboarding && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    type="text"
                    value={onboardingForm.customPassword}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, customPassword: e.target.value })}
                    placeholder="MyPassword123!"
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    ✅ Password requirements:
                    <ul style={{ marginLeft: 20, marginTop: 4 }}>
                      <li>At least 8 characters</li>
                      <li>At least one uppercase letter (A-Z)</li>
                      <li>At least one lowercase letter (a-z)</li>
                      <li>At least one number (0-9)</li>
                      <li>At least one special character (!@#$%^&*)</li>
                    </ul>
                    💡 Example: Welcome2024! or ArchSystems123!
                  </Typography>
                </Box>
              )}
              {!useCustomPasswordOnboarding && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  If unchecked, a random secure password will be generated automatically
                </Typography>
              )}
            </Box>

            {/* Permanent Password Option */}
            <Box sx={{ border: '1px solid #A5D6A7', borderRadius: 1, p: 2, bgcolor: '#E8F5E9' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permanentPasswordOnboarding}
                    onChange={(e) => setPermanentPasswordOnboarding(e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      🔒 Make Password Permanent
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {permanentPasswordOnboarding
                        ? '✅ Permanent: User can log in immediately without changing password'
                        : '⚠️ Temporary: User must change password on first login'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Role and Organization */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Role & Organization</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    required
                    fullWidth
                    label="Role"
                    value={onboardingForm.role}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, role: e.target.value })}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="inspector">Inspector</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Admin</option>
                    <option value="program_admin">Program Admin</option>
                    <option value="content_creator">Content Creator</option>
                    <option value="mac_npe_reviewers">MAC/NPE Reviewer</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Organization Name"
                    value={onboardingForm.organizationName}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, organizationName: e.target.value })}
                    placeholder="e.g., Arch Systems Inc."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Location Information */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Location Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Region"
                    value={onboardingForm.region}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, region: e.target.value })}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select Region</option>
                    <option value="northeast">Northeast</option>
                    <option value="southeast">Southeast</option>
                    <option value="midwest">Midwest</option>
                    <option value="southwest">Southwest</option>
                    <option value="west">West</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={onboardingForm.zipCode}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, zipCode: e.target.value })}
                    placeholder="12345"
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={onboardingForm.city}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, city: e.target.value })}
                    placeholder="e.g., New York"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    value={onboardingForm.state}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, state: e.target.value })}
                    placeholder="e.g., NY"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* System Access */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>System Access</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={onboardingForm.lmsEnabled}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, lmsEnabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="LMS Enabled"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={onboardingForm.rmsEnabled}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, rmsEnabled: e.target.checked })}
                      color="secondary"
                    />
                  }
                  label="RMS Enabled"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={onboardingForm.rmsUnlocked}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, rmsUnlocked: e.target.checked })}
                      color="warning"
                    />
                  }
                  label="RMS Unlocked"
                />
              </Box>
            </Box>

            {/* Email Notification */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={onboardingForm.sendWelcomeEmail}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, sendWelcomeEmail: e.target.checked })}
                    color="primary"
                  />
                }
                label="Send welcome email with login credentials"
              />
            </Box>

            {/* Result Display */}
            {onboardingResult && (
              <Box sx={{ p: 2, bgcolor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: '#2E7D32', fontWeight: 'bold' }}>
                  <PersonAddIcon />
                  User Onboarded Successfully!
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Email:</Typography>
                    <Typography variant="body2">{onboardingResult.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">User ID:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{onboardingResult.user_id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>Generated Password:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        fullWidth
                        type={showOnboardingPassword ? 'text' : 'password'}
                        value={onboardingResult.password}
                        InputProps={{
                          readOnly: true,
                          sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                        }}
                      />
                      <IconButton onClick={() => setShowOnboardingPassword(!showOnboardingPassword)} size="small">
                        {showOnboardingPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton onClick={() => copyToClipboard(onboardingResult.password)} size="small" color="primary">
                        <CopyIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FFF9C4', border: '1px solid #FBC02D', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      ⚠️ <strong>Important:</strong> Save this password securely. The user will need it to log in for the first time.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOnboardingCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleOnboardingSubmit} color="success" variant="contained" startIcon={<PersonAddIcon />}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* User History Dialog */}
      {userForHistory && (
        <UserHistoryDialog
          open={historyDialogOpen}
          onClose={() => {
            setHistoryDialogOpen(false);
            setUserForHistory(null);
          }}
          userId={userForHistory.id}
          userName={`${userForHistory.first_name} ${userForHistory.last_name}`}
        />
      )}

      {/* User Tasks Dialog */}
      {userForTasks && (
        <UserTasksDialog
          open={tasksDialogOpen}
          onClose={() => {
            setTasksDialogOpen(false);
            setUserForTasks(null);
          }}
          userId={userForTasks.id}
          userName={`${userForTasks.first_name} ${userForTasks.last_name}`}
        />
      )}
    </Box>
  );
};

